import {Controller, FieldArrayWithId, UseFormReturn} from 'react-hook-form'
import {BulkOrderFormValues} from '../../BulkOrdersFormValues'
import {
  ActiveMaterialDeliveryItem,
  OrderIntakeOption,
  OrderIntakeOptions,
  OrderRequest,
  OrdersFormValidityState,
  PersistedOrderRequest,
  SlotConfiguration
} from '../../declarations/types'
import {useBulkOrderFormChangeHandlerWithMethods} from '../../FormChangeHandlers'
import {
  PersistedMaterialsAction,
  PersistedMaterialsBanner
} from '../../components/PersistedMaterialsDialog/PersistedMaterialsBanner'
import {useMaterialEditorController} from '../hooks/useMaterialEditorController'
import {Box, Grid, Hidden, Paper, Stack, Table, TableBody, TableContainer} from '@mui/material'
import {MaterialSectionHeader, MaterialsHeader} from '../../components/VisualComponents'
import {SiteDropdown} from '../../components/SiteDropdown'
import {partialRight} from 'lodash'
import {ShippingType} from '../../declarations/OrderIntake.enums'
import {invalidateHubQuery} from '../../../common/react-query/QueryProvider'
import distributedCacheLoadingImage from '../../../Assets/distributedCacheLoading.svg'
import {Typography} from '@hconnect/uikit'
import {getDefaultMaterialOptions, isButtonDisabled} from '../../../Organisms/OrderIntake/utils'
import {DeliverMaterialForm} from './DeliverMaterialForm'
import {AddAnotherButton} from '../../components/AddAnotherButton'
import {ContactSection} from '../containers/ContactSection'
import noValidMaterialImage from '../../../Assets/noValidMaterials/no-delivery-placeholder.svg'
import {BusinessLineType} from '../../../common/types'
import {InvalidOrderIntakeOptions} from '../../Options/query'

interface DeliverMainContentProps {
  // Site selection
  methods: UseFormReturn<BulkOrderFormValues>
  siteOptions: OrderIntakeOption[]
  deliverOptions: OrderIntakeOptions
  defaultDelivery: OrderRequest | InvalidOrderIntakeOptions
  formChangeHandlers: ReturnType<typeof useBulkOrderFormChangeHandlerWithMethods>
  customerId?: string
  applyMaterialEnteredDescription: boolean
  applyContractItemDescription: boolean

  // Persisted materials
  isMaterialPersistency: boolean
  persistedOrderRequests: PersistedOrderRequest[] | undefined
  persistedMaterialsBanner: {
    showBanner: boolean
    existsValidPersistedMaterials: boolean
  }
  onClosePersistedMaterialsBanner: (action: PersistedMaterialsAction) => void

  // Materials list
  selectedSite: OrderIntakeOption
  orderFields: FieldArrayWithId<BulkOrderFormValues, 'orders'>[]
  materialEditorController: ReturnType<typeof useMaterialEditorController>
  isSlotsManagementEnabled: boolean
  useConfigurableSlots: boolean
  slotConfiguration: SlotConfiguration[] | undefined
  ordersFormValidity: OrdersFormValidityState[]
  isAllOrdersValid: boolean
  isOrderRequestsCacheLoading: boolean
  noValidMaterial: boolean
  defaultBusinessLine: BusinessLineType
  initialOrderCustomerReference: string

  // Callbacks
  addDelivery: () => void
  removeDelivery: (index: number) => void
  handleEditorActivation: (id: string, item: ActiveMaterialDeliveryItem, index: number) => void
  setIsEditOrderButtonClicked: (val: boolean) => void

  // Layout
  isMobile: boolean
  t: any
  classes: any
  orders: OrderRequest[]
}

export const DeliverMainContent = (props: DeliverMainContentProps) => {
  const {
    methods,
    siteOptions,
    deliverOptions,
    defaultDelivery,
    formChangeHandlers,
    customerId,
    applyMaterialEnteredDescription,
    applyContractItemDescription,
    isMaterialPersistency,
    persistedOrderRequests,
    persistedMaterialsBanner: persistedMaterials,
    onClosePersistedMaterialsBanner,
    selectedSite,
    orderFields,
    materialEditorController,
    isSlotsManagementEnabled,
    useConfigurableSlots,
    slotConfiguration,
    ordersFormValidity,
    isAllOrdersValid,
    isOrderRequestsCacheLoading,
    noValidMaterial,
    defaultBusinessLine,
    initialOrderCustomerReference,
    addDelivery,
    removeDelivery,
    handleEditorActivation,
    setIsEditOrderButtonClicked,
    isMobile,
    t,
    classes,
    orders
  } = props

  const isDisabled = isButtonDisabled(
    isOrderRequestsCacheLoading,
    initialOrderCustomerReference,
    orders
  )

  return (
    <Grid item xs={12} md={8} style={{paddingRight: 0}}>
      <Paper
        style={isMobile ? {borderRadius: 0, background: '#F5F8FB', padding: 16} : {}}
        className={classes.paperStyle}
      >
        <MaterialSectionHeader data-test-id="delivery-site-selection-section-header">
          {t('orderIntake.deliveryAddress')}
        </MaterialSectionHeader>
        <Grid item xs={12} sm={8}>
          <Controller
            control={methods.control}
            name="selectedSite"
            render={({field}) => (
              <SiteDropdown
                onChange={partialRight(
                  formChangeHandlers.onSiteChange,
                  deliverOptions,
                  defaultDelivery
                )}
                selectedSite={field.value}
                shippingType={ShippingType.DELIVER}
                options={siteOptions}
                fetchOrderIntakeOptions={() =>
                  invalidateHubQuery('orderRequestsOptions', {
                    customerId,
                    applyMaterialEnteredDescription,
                    applyContractItemDescription
                  })
                }
              />
            )}
          />
        </Grid>

        {isMaterialPersistency &&
          persistedOrderRequests &&
          persistedMaterials.showBanner &&
          !noValidMaterial && (
            <PersistedMaterialsBanner
              onClose={onClosePersistedMaterialsBanner}
              existsValidMaterials={persistedMaterials.existsValidPersistedMaterials}
            />
          )}

        {!noValidMaterial ? (
          isOrderRequestsCacheLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" marginTop={10}>
              <img
                src={distributedCacheLoadingImage}
                data-test-id="order-intake-loading-data-from-cache"
                style={{textAlign: 'center', maxWidth: 350}}
                alt={t('orderIntake.loadingDataFromCache')}
              />
              <Box marginTop={2}>
                <Typography
                  variant="body2"
                  color="secondary"
                  style={{textAlign: 'center', maxWidth: 350}}
                >
                  {t('orderIntake.loadingDataFromCache')}
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Stack marginTop={4}>
                <MaterialSectionHeader
                  style={{marginTop: 20}}
                  data-test-id="delivery-material-selection-section-header"
                >
                  {t('orderIntake.cementTypeDelivery')}
                </MaterialSectionHeader>
                <TableContainer style={{marginBottom: 20}}>
                  <Table>
                    <Hidden smDown>
                      <MaterialsHeader
                        showHaulierInformation={false}
                        shippingType={ShippingType.DELIVER}
                        isAllOrdersValid={isAllOrdersValid}
                      />
                    </Hidden>
                    <TableBody>
                      {orderFields.map((delivery, index) => {
                        const defaultMaterialOption = getDefaultMaterialOptions(
                          selectedSite.materials,
                          delivery.payload
                        )
                        const {status, isSuccess} = materialEditorController.getMaterialFormProps(
                          delivery.id
                        )
                        if (!defaultMaterialOption) return null

                        return (
                          <DeliverMaterialForm
                            key={delivery.id}
                            selectedSite={selectedSite}
                            defaultMaterialOption={defaultMaterialOption}
                            orderIndex={index}
                            removeDelivery={() => removeDelivery(index)}
                            isSuccess={isSuccess}
                            status={status}
                            checkValidityBySlots={isSlotsManagementEnabled && useConfigurableSlots}
                            slotConfiguration={slotConfiguration}
                            onActivate={(item) => {
                              setIsEditOrderButtonClicked(true)
                              handleEditorActivation(delivery.id, item, index)
                            }}
                            OrdersFormValidityState={ordersFormValidity}
                            isAllOrdersValid={isAllOrdersValid}
                          />
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <AddAnotherButton
                  businessLine={defaultBusinessLine}
                  disabled={isDisabled}
                  onClick={addDelivery}
                />
              </Stack>
              <Box marginTop={2}>
                <ContactSection shippingType={ShippingType.DELIVER} dataTestPrefix="delivery" />
              </Box>
            </>
          )
        ) : null}
        {noValidMaterial ? (
          <Box display="flex" flexDirection="column" alignItems="center" marginTop={10}>
            <img
              src={noValidMaterialImage}
              data-test-id="order-intake-no-valid-ship-to-image"
              style={{textAlign: 'center', maxWidth: 350}}
              alt={t('orderIntake.invalidMaterials.deliveryNoPossibleForDestination')}
            />
            <Box marginTop={2}>
              <Typography
                variant="body2"
                color="secondary"
                style={{textAlign: 'center', maxWidth: 350}}
              >
                {t('orderIntake.invalidMaterials.deliveryNoPossibleForDestinationMessage')}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </Paper>
    </Grid>
  )
}
